import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { FreelancerSubunitResource } from '@/types/resources';
import { api } from '@/utils/api';

import FreelancerSubunitFormModal from './FreelancerSubunitFormModal';

interface Props {
  subunit: FreelancerSubunitResource;
}

const FreelancerSubunitTableRow: FC<Props> = ({ subunit }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this subunit?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/freelancer-subunits/${subunit.id}`,
      );

      await queryClient.invalidateQueries(['freelancer_subunits'], {
        exact: false,
      });

      alerts.success('Subunit was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <>
      <tr>
        <Td>{subunit.name}</Td>
        <Td>{subunit.company?.name ?? '-'}</Td>
        <Td>{subunit.hide_from_dropdowns ? 'Yes' : 'No'}</Td>
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowEditModal(true)}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
      </tr>
      <AnimatePresence>
        {showEditModal && (
          <FreelancerSubunitFormModal
            subunit={subunit}
            onClose={() => {
              setShowEditModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FreelancerSubunitTableRow;
